import { useCallback } from 'react'

import { useUnit } from 'effector-react'

import BackIcon from '@/assets/icons/icon-back.svg'
import MoreIcon from '@/assets/icons/icon-more.svg'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $hasEditVaultPermission } from '@/models/permissions.ts'
import {
  $selectedCategory,
  setSelectedCategory,
} from '@/models/vault/categories.ts'
import { $mediaQueryParam } from '@/models/vault/medias/options.ts'
import { closeMediaCategory } from '@/models/vault/vault.ts'

import { VaultMediaSearchBox } from './vault-media-search-box'

interface Props {
  showAddMedia?: boolean
}

export const VaultMediaTitle = ({ showAddMedia = true }: Props) => {
  const hasEditVaultPermission = useUnit($hasEditVaultPermission)
  const selectedCategory = useUnit($selectedCategory)
  const mediaQuery = useUnit($mediaQueryParam)

  const handleBack = useCallback(() => {
    setSelectedCategory(null)
    closeMediaCategory()
  }, [])

  const handleAddMediaClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.ADD_MEDIA,
      selectedCategoryId: selectedCategory?.id,
    })
  }, [selectedCategory?.id])

  const handleVaultRenameCategoryDialogOpen = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.VAULT_RENAME_CATEGORY,
    })
  }, [])

  const handleVaultDeleteCategoryDialogOpen = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.VAULT_DELETE_CATEGORY,
    })
  }, [])

  return (
    <div className="flex flex-col gap-2 items-stretch border-b max-lg:border-transparent pb-4">
      <div className="flex flex-1 justify-between pl-0 lg:pl-3">
        <div className="flex items-center">
          <div className="visible lg:hidden">
            <Button
              onClick={handleBack}
              className="text-text-default mr-2"
              variant="ghost"
            >
              <BackIcon />
            </Button>
          </div>
          <p className="text-xl font-medium max-w-full truncate">
            {mediaQuery
              ? 'Results'
              : selectedCategory
                ? selectedCategory.name
                : 'All Media'}
          </p>
          {selectedCategory &&
            hasEditVaultPermission &&
            (selectedCategory?.canUpdate || selectedCategory?.canDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger className="ml-2">
                  <MoreIcon className="h-5 w-5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-50 px-[5px] py-[8px] z-100"
                >
                  {selectedCategory.canUpdate && (
                    <DropdownMenuItem
                      onClick={handleVaultRenameCategoryDialogOpen}
                    >
                      Rename Category
                    </DropdownMenuItem>
                  )}
                  {selectedCategory.canDelete && (
                    <DropdownMenuItem
                      onClick={handleVaultDeleteCategoryDialogOpen}
                    >
                      Delete Category
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
        </div>
        <div className="flex items-center gap-2 pl-0 lg:flex-row lg:pl-3">
          <VaultMediaSearchBox className="min-w-[120px] max-w-[250px]" />
          {showAddMedia && hasEditVaultPermission && (
            <Button
              size="sm"
              onClick={handleAddMediaClick}
              className="min-[1024px]:max-[1300px]:hidden max-[600px]:hidden shrink-0"
            >
              <PlusIcon /> Add Media
            </Button>
          )}
        </div>
      </div>
      {showAddMedia && hasEditVaultPermission && (
        <Button
          size="sm"
          onClick={handleAddMediaClick}
          className="hidden min-[1024px]:max-[1300px]:inline-flex ml-0 lg:ml-3 max-[600px]:inline-flex"
        >
          <PlusIcon /> Add Media
        </Button>
      )}
    </div>
  )
}
